<script lang="ts">
  import { ParticipantService } from '$src/js/modules/participants/services/ParticipantService';
  import FormField from '$src/js/modules/ui/components/form/FormField.svelte';
  import Svg from '$src/js/modules/ui/components/Svg.svelte';
  import type { ParticipantType } from '$src/types/participants';
  import { defaultConfig } from '$src/js/modules/form-validation/init-bouncer';
  import Bouncer from 'formbouncerjs';
  import Radio from '$src/js/modules/ui/components/form/Radio.svelte';

  const {
    participant,
    newParticipant,
    loading,
    theme,
    selectable,
    onClose,
    onSave,
    onDelete,
    onSelect,
  }: {
    participant?: ParticipantType;
    newParticipant?: boolean;
    loading?: boolean;
    theme: 'yellow' | 'green';
    selectable?: boolean;
    onClose?: () => void;
    onSave?: (data: ParticipantType) => Promise<void>;
    onDelete?: (id: number | null) => void;
    onSelect?: (id: number | null, name: string) => void;
  } = $props();

  const uniqueIdentifier = `participant-form-${Math.random().toString(36).substring(2, 15)}`;

  let changeable = $state(newParticipant ?? false);
  const participantService = new ParticipantService();

  const emptyParticipant: ParticipantType = {
    city: '',
    dateOfBirth: '',
    firstName: '',
    gender: '',
    id: null,
    lastName: '',
  };

  const originalParticipant: ParticipantType = participant
    ? (JSON.parse(JSON.stringify(participant)) as ParticipantType)
    : (JSON.parse(JSON.stringify(emptyParticipant)) as ParticipantType);

  let participantData = $state(
    JSON.parse(JSON.stringify(originalParticipant)) as ParticipantType
  );

  const resetToDefault = () => {
    participantData = JSON.parse(
      JSON.stringify(originalParticipant)
    ) as ParticipantType;
    changeable = false;

    // Reset selection when cancelling edit
    onSelect?.(null, '');
    onClose?.();
  };

  const saveParticipant = async () => {
    const form = document.querySelector(
      `form[data-bouncer-identifier="${uniqueIdentifier}"]`
    ) as HTMLFormElement | undefined;

    if (form) {
      const readonlyInputs: NodeListOf<HTMLInputElement> =
        form.querySelectorAll('input[readonly]');

      const validator = new Bouncer(
        `form[data-bouncer-identifier="${uniqueIdentifier}"]`,
        defaultConfig
      );

      readonlyInputs.forEach((input: HTMLInputElement) => {
        input.readOnly = false;
      });

      const errors = validator.validateAll(form);

      readonlyInputs.forEach((input: HTMLInputElement) => {
        input.readOnly = true;
      });

      if (errors.length > 0) {
        return;
      }

      validator.destroy();
    }

    const dataToSave: ParticipantType = {
      city: participantData.city,
      dateOfBirth: participantData.dateOfBirth,
      firstName: participantData.firstName,
      gender: participantData.gender,
      id: participantData.id ?? null,
      lastName: participantData.lastName,
    };

    if (onSave) {
      await onSave(dataToSave);
      // Reset selection after saving changes
      onSelect?.(null, '');
    }

    resetToDefault();
  };

  const deleteParticipant = () => {
    if (onDelete) {
      // Reset selection before deleting
      onSelect?.(null, '');
      onDelete(participantData.id);
    }
  };
</script>

<fieldset class="border-white-1000 border-1">
  {#if changeable}
    <form
      method="post"
      class={`2xl:fluid-gap-[32] flex flex-col gap-8 px-8 py-8 lg:px-12 lg:py-16 ${loading ? 'loading-box' : ''}`}
      data-bouncer-identifier={uniqueIdentifier}
    >
      <div class="2xl:fluid-gap-[16] flex w-full gap-4 max-sm:contents">
        <FormField
          background="black"
          type="text"
          label="Voornaam"
          id="firstname"
          name="firstname"
          required={true}
          bind:value={participantData.firstName}
        />

        <FormField
          background="black"
          type="text"
          label="Achternaam"
          id="lastname"
          name="lastname"
          required={true}
          bind:value={participantData.lastName}
        />
      </div>

      <FormField
        background="black"
        type="select"
        label="Geslacht"
        id="gender"
        name="gender"
        required={true}
        options={Object.entries(window.genderOptions).map(([key, label]) => ({
          value: key,
          label,
        }))}
        bind:value={participantData.gender}
      />

      <FormField
        background="black"
        type="text"
        label="Woonplaats"
        id="city"
        name="city"
        required={true}
        bind:value={participantData.city}
      />

      <FormField
        background="black"
        type="dateOfBirth"
        label="Geboortedatum"
        id={`dob-${Math.random().toString(36).substring(2, 9)}`}
        name="dateOfBirth"
        attributes={{
          dateObjects: participantData.dateOfBirth,
        }}
        required={true}
        bind:value={participantData.dateOfBirth}
      />

      <div
        class="flex flex-col justify-center gap-4 sm:flex-row sm:items-center md:justify-between"
      >
        <button
          class={theme === 'green' ? 'btn-green-large' : 'btn-primary-large'}
          type="button"
          onclick={saveParticipant}
        >
          {#if newParticipant}
            Deelnemer toevoegen
          {:else}
            Wijzigen
          {/if}
        </button>

        <div class="flex gap-4">
          <button
            class={`${theme === 'green' ? 'btn-underline-green' : 'btn-underline'} self-center`}
            type="button"
            onclick={resetToDefault}>Annuleer</button
          >

          {#if !newParticipant}
            <button
              type="button"
              class={`${theme === 'green' ? 'btn-underline-green' : 'btn-underline'} self-center`}
              onclick={deleteParticipant}>Verwijder</button
            >
          {/if}
        </div>
      </div>
    </form>
  {:else if participant}
    <label
      class="2xl:fluid-gap-[16] 2xl:fluid-p-[24] flex flex-col items-center gap-4 p-6 lg:flex-row"
    >
      <div
        class="2xl:fluid-gap-[24] flex w-full flex-1 flex-row items-center gap-6"
      >
        {#if selectable}
          <div class="2xl:fluid-w-[20] w-5">
            <Radio
              background="black"
              id="participant"
              name="participant[]"
              value={participant.id?.toString() ?? ''}
              onChange={() => {
                if (participant.id !== null) {
                  onSelect?.(
                    participant.id,
                    participant.firstName + ' ' + participant.lastName
                  );
                }
              }}
            />
          </div>
        {/if}
        <div class="flex flex-1 flex-col gap-4">
          <p class="text-18px leading-[100%] font-medium">
            {participantData.firstName}
            {participantData.lastName}
          </p>
          <p
            class="text-14px 2xl:fluid-gap-[16] flex flex-col gap-1 lg:flex-row lg:gap-4"
          >
            <span
              >Geb. op {participantService.displayDateOfBirth(
                participantData.dateOfBirth
              )}</span
            >
            <span
              >{participantService.renderGender(participantData.gender)}</span
            >
          </p>
        </div>
      </div>
      <div class="flex">
        <button
          class={theme === 'green'
            ? 'btn-underline-green-small'
            : 'btn-underline-small'}
          onclick={() => (changeable = true)}
        >
          <span>Aanpassen</span>
          <Svg
            image="pencil"
            width={18}
            height={18}
            className="2xl:fluid-w-[18] 2xl:fluid-h-[18] fill-white-1000 transition"
          />
        </button>
      </div>
    </label>
  {/if}
</fieldset>
